/**
 * Allstar self-takeover fallbacks for the branded iframe.
 *
 * Live ad campaigns are resolved server-side by partner-api (backed by
 * Ghostface) and arrive on the playlist response as `data.adCampaign`. The
 * iframe itself no longer carries a mock catalog or a client-side resolver
 * — when the server returns no campaign for the viewer's geo, we fall
 * through to the constants below so the embed never reads as fully
 * unbranded (every slot still shows Allstar attribution + clickthrough).
 */
import type { Creative, ResolvedSponsorConfig } from "./@types";

export const ALLSTAR_FALLBACK_UPPER_LEFT: Creative = {
  id: "allstar-fallback-ul-logo",
  assetUrl: "inline:allstar-wordmark",
  altText: "Allstar",
  ctaUrl:
    "https://allstar.gg/?utm_source=hltv-iframe&utm_medium=ul-logo&utm_campaign=allstar-fallback",
};

export const ALLSTAR_FALLBACK_LOWER_RIGHT: Creative = {
  id: "allstar-fallback-lr-logo",
  assetUrl: "inline:allstar-wordmark",
  altText: "Allstar",
  ctaUrl:
    "https://allstar.gg/?utm_source=hltv-iframe&utm_medium=lr-logo&utm_campaign=allstar-fallback",
};

/**
 * Full Allstar self-takeover served when partner-api returns no paid
 * sponsor campaign for the viewer's geo. Fills every slot (UL watermark,
 * LR chrome wordmark, top banner, endcard) with our own brand promoting
 * the /brand sales page.
 *
 * Copy is shaped to the Ad Campaign Creative Intake form constraints
 * (banner headline ≤25, subtitle ≤29, CTA ≤20, endcard headline ≤35) so
 * it doubles as a reference example for partners filling out that form.
 */
const ALLSTAR_SELF_TAKEOVER_UTM =
  "utm_source=hltv-iframe&utm_campaign=allstar-self-takeover";
const ALLSTAR_ENDCARD_IMAGE =
  "https://media.allstar.gg/static/iframe_brands/examples/endcard.png";

export const ALLSTAR_FALLBACK_SPONSOR_CONFIG: ResolvedSponsorConfig = {
  campaignId: "allstar-self-takeover",
  brandName: "Allstar",
  slots: {
    upperLeftLogo: ALLSTAR_FALLBACK_UPPER_LEFT,
    lowerRightLogo: ALLSTAR_FALLBACK_LOWER_RIGHT,
    topBanner: {
      id: "allstar-self-top-banner",
      // Use the real standalone Allstar "A" mark (AMarkSvg) for the
      // banner's leading icon, NOT the generic star-in-tile fallback
      // (`inline:allstar-icon-tile`) nor the wide wordmark.
      assetUrl: "inline:allstar-mark",
      altText: "Allstar",
      ctaUrl: `https://allstar.gg/brand?${ALLSTAR_SELF_TAKEOVER_UTM}&utm_medium=top-banner`,
      appearAfterMs: 5000,
      dismissable: true,
      dismissTimeoutMs: 3000,
      // Copy intentionally pegged at the partner intake form's max
      // character limits so layout edge-cases get exercised by the
      // default takeover:
      //   - title: 25/25
      //   - subtitle: 29/29
      //   - ctaLabel: 15/15
      title: "Auto-Save Your Best Plays",
      subtitle: "allstar.gg/start-clipping-now",
      ctaLabel: "Start Clipping!",
    },
    endcard: {
      id: "allstar-self-endcard",
      assetUrl: ALLSTAR_ENDCARD_IMAGE,
      altText: "Get Your Automatic Highlights Today — Allstar",
      ctaUrl: `https://allstar.gg/brand?${ALLSTAR_SELF_TAKEOVER_UTM}&utm_medium=endcard`,
      autoAdvanceMs: 5000,
      expandOnHover: true,
      title: "Get Your Automatic Highlights Today",
    },
  },
  // Intentionally no `theme` — let the iframe fall back to its baseline
  // accent (#3C87D2 Allstar blue) so the default takeover ships creative
  // copy + assets without overriding the embed's chrome colors.
};
